// M30 — Lightweight offline indicator.
//
// Sits above the route outlet and renders an Alert when the browser
// reports it has lost connectivity. Most "thing didn't save" failures
// during alpha will be Supabase errors that surface via the per-action
// toast (M18); this banner is the catch-all for the "wait, why isn't
// anything working?" moment when the user is offline and doesn't
// realise it yet.
//
// We rely on `navigator.onLine` + the matching `online` / `offline`
// window events rather than an active Supabase health check. Reasons:
//   - Browser signals are cheap and don't add network traffic.
//   - An active health check on mount would give false positives
//     (transient CORS, cold Supabase pool warm-up) and miss intermittent
//     drops after mount anyway.
//   - The error toasts already cover the "I clicked save and it failed"
//     case where the server is reachable but the request fails.
//
// ── M94 follow-on: THE BANNER USED TO GET STUCK ─────────────────────────────
//
// Found during M94b2 QA: the banner was showing while `navigator.onLine` read
// `true` and Supabase calls were completing normally in the same page.
//
// The cause was the shape of the state, not the signal. `online` was seeded
// from `navigator.onLine` ONCE on mount and thereafter only moved when an
// event fired. Events are not guaranteed to arrive in pairs — a backgrounded
// tab, a suspended machine, a device-emulation quirk can all deliver the
// `offline` and lose the `online` — and nothing ever re-read the property. One
// missed event and the banner lies until a full reload, which is the worst
// possible failure for a control whose entire job is telling the truth about
// connectivity.
//
// So the property is now the source of truth and the events are only prompts
// to re-read it. `visibilitychange` and `focus` join them, because "the user
// is looking at the page again" is exactly when a stale banner does its damage
// and exactly when the browser can tell us the truth.
//
// Still no polling and still no reachability check — see the reasons above.
// Those were right and they have not changed; what changed is that a signal we
// already had is now read when it matters instead of once.

import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import WifiOffOutlined from '@mui/icons-material/WifiOffOutlined';

/** `navigator.onLine`, defensively. SSR and odd embeddings have no navigator,
 *  and "we cannot tell" must read as online — a banner shown to someone who is
 *  connected is the failure this component was fixed for. */
function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export default function NetworkStatusBanner() {
  // `navigator.onLine` is reasonably trustworthy: it's false when the
  // OS has no network. It's NOT a substitute for a real reachability
  // check (a Wi-Fi captive-portal will look online but block traffic),
  // but it's the right shape for "user obviously can't reach us."
  const [online, setOnline] = useState<boolean>(readOnline);

  useEffect(() => {
    // ONE handler for all four events. Each of them means "read the property
    // again", never "set the state to this constant": a handler that hardcodes
    // its own answer can disagree with the property it is supposed to reflect,
    // which is how the stale banner survived an `online` event that did arrive.
    const sync = () => setOnline(readOnline());

    // No throttle, deliberately. `focus` and `visibilitychange` both fire on a
    // single tab switch — the same double-fire the inventory tab has to guard
    // — but there the handler runs nine queries and here it sets a boolean to
    // the value it probably already has, which React drops without a re-render.
    // A throttle would be machinery protecting nothing.
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);

    // And once on mount, after the initial state was seeded: a tab restored
    // from the back/forward cache can mount with a value that was true when it
    // was frozen.
    sync();

    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  // CONDITIONALLY RENDERED, not collapsed to zero height.
  //
  // Two reasons, and the second is why the first was not enough. The Alert
  // renders `role="alert"`, and a live region parked in the accessibility tree
  // at zero height is a thing screen readers may announce at any time — the
  // warning should exist exactly when it is true. And `Collapse`'s own
  // `unmountOnExit` does not achieve that here: it unmounts on `onExited`,
  // which waits for a transition that never runs outside a real browser, so
  // the banner stayed in the DOM after going away. The same shape as M71's
  // zero-height Collapse: when presence is the thing that matters, render
  // conditionally and let Collapse animate the arrival.
  if (online) return null;

  return (
    <Collapse in appear>
      <Alert
        severity="warning"
        icon={<WifiOffOutlined fontSize="inherit" />}
        square
        sx={{ borderRadius: 0 }}
      >
        You're offline. Anything you change won't sync until you're back online.
      </Alert>
    </Collapse>
  );
}
